/** Public runtime configuration injected into the browser. */
export type PublicRuntimeEnvKey =
  | "NEXT_PUBLIC_BASE_URL"
  | "NEXT_PUBLIC_FAMILY_WEB_URL"
  | "NEXT_PUBLIC_MUI_KEY"
  | "NEXT_PUBLIC_GOOGLE_MAPS_API_KEY"
  | "NEXT_PUBLIC_GOOGLE_MAP_ID"
  | "NEXT_PUBLIC_NODE_ENV"
  | "NEXT_PUBLIC_LOG_LEVEL"
  | "NEXT_PUBLIC_FARO_URL"
  | "NEXT_PUBLIC_APP_VERSION"
  | "NEXT_PUBLIC_OBSERVABILITY_TENANT"
  | "NEXT_PUBLIC_OBSERVABILITY_TENANT_HEADER"
  | "NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY"
  | "NEXT_PUBLIC_FLAGSMITH_API_URL"
  | "NEXT_PUBLIC_FLAGSMITH_ENABLE_ANALYTICS"
  | "NEXT_PUBLIC_SESSION_IDLE_TIMEOUT_MINUTES"
  | "NEXT_PUBLIC_SESSION_TIMEOUT_COUNTDOWN_SECONDS";

type PublicRuntimeEnv = Partial<Record<PublicRuntimeEnvKey, string>>;

function readWindowEnv(key: PublicRuntimeEnvKey): string | undefined {
  if (typeof window === "undefined" || !window.ENV) {
    const envVal = process.env[key];
    return envVal && envVal.length > 0 ? envVal : undefined;
  }

  const value = window.ENV[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export function getPublicEnv(key: PublicRuntimeEnvKey, fallback = ""): string {
  return readWindowEnv(key) ?? fallback;
}

export function getPublicRuntimeEnv(): PublicRuntimeEnv {
  return {
    NEXT_PUBLIC_BASE_URL: getPublicEnv("NEXT_PUBLIC_BASE_URL"),
    NEXT_PUBLIC_FAMILY_WEB_URL: getPublicEnv("NEXT_PUBLIC_FAMILY_WEB_URL"),
    NEXT_PUBLIC_MUI_KEY: getPublicEnv("NEXT_PUBLIC_MUI_KEY"),
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: getPublicEnv(
      "NEXT_PUBLIC_GOOGLE_MAPS_API_KEY"
    ),
    NEXT_PUBLIC_GOOGLE_MAP_ID: getPublicEnv("NEXT_PUBLIC_GOOGLE_MAP_ID"),
    NEXT_PUBLIC_NODE_ENV: getPublicEnv("NEXT_PUBLIC_NODE_ENV"),
    NEXT_PUBLIC_LOG_LEVEL: getPublicEnv("NEXT_PUBLIC_LOG_LEVEL"),
    NEXT_PUBLIC_FARO_URL: getPublicEnv("NEXT_PUBLIC_FARO_URL"),
    NEXT_PUBLIC_APP_VERSION: getPublicEnv("NEXT_PUBLIC_APP_VERSION"),
    NEXT_PUBLIC_OBSERVABILITY_TENANT: getPublicEnv(
      "NEXT_PUBLIC_OBSERVABILITY_TENANT"
    ),
    NEXT_PUBLIC_OBSERVABILITY_TENANT_HEADER: getPublicEnv(
      "NEXT_PUBLIC_OBSERVABILITY_TENANT_HEADER"
    ),
    NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY: getPublicEnv(
      "NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY"
    ),
    NEXT_PUBLIC_FLAGSMITH_API_URL: getPublicEnv(
      "NEXT_PUBLIC_FLAGSMITH_API_URL"
    ),
    NEXT_PUBLIC_FLAGSMITH_ENABLE_ANALYTICS: getPublicEnv(
      "NEXT_PUBLIC_FLAGSMITH_ENABLE_ANALYTICS"
    ),
    NEXT_PUBLIC_SESSION_IDLE_TIMEOUT_MINUTES: getPublicEnv(
      "NEXT_PUBLIC_SESSION_IDLE_TIMEOUT_MINUTES"
    ),
    NEXT_PUBLIC_SESSION_TIMEOUT_COUNTDOWN_SECONDS: getPublicEnv(
      "NEXT_PUBLIC_SESSION_TIMEOUT_COUNTDOWN_SECONDS"
    ),
  };
}

export function getPublicNodeEnv(): string {
  return getPublicEnv(
    "NEXT_PUBLIC_NODE_ENV",
    process.env.NODE_ENV ?? "development"
  ).toLowerCase();
}
